import type { SuggestionOptions } from "@tiptap/suggestion";
import type { EmojiEntrySkin } from "../../emojis/data";
import type { SuggestionPluginState } from "../suggestions/suggestionRenderer";
import type { EmojiAttributes, EmojiAttributeSpec } from "./types";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import { combineTransactionSteps, findChildrenInRange, getChangedRanges, mergeAttributes, Node } from "@tiptap/vue-3";
import EmojiRegex from "emoji-regex-xs";
import { isString } from "@/common/general";
import { emojiIndex } from "../../emojis/data";
import { createSuggestionRenderer } from "../suggestions";
import EmojiSearchList from "./EmojiSearchList.vue";

export interface EmojiOptions {
  suggestion: Omit<SuggestionOptions, "editor">;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    emoji: {
      /**
       * Starts emoji suggestion at current location
       */
      startEmoji: () => ReturnType;
      /**
       * Inserts emoji at current location
       */
      insertEmoji: (emoji: EmojiEntrySkin) => ReturnType;
    };
  }
}

export const EmojiPluginKey = new PluginKey("emoji");
export const EmojiSuggestionPluginKey = new PluginKey<SuggestionPluginState>("emoji-suggestion");

const emojiRegex = EmojiRegex();

export const Emojis = Node.create<EmojiOptions>({
  name: "emoji",
  group: "inline",
  inline: true,
  selectable: false,
  atom: true,

  // increase priority, so that keydown events will be processed in here first before Paragraph node
  priority: 10000,

  addOptions() {
    return {
      suggestion: {
        char: ":",
        pluginKey: EmojiSuggestionPluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(" ");

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props as EmojiAttributes,
              },
              {
                type: "text",
                text: " ",
              },
            ])
            .run();
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          const allow = !!type && !!$from.parent.type.contentMatch.matchType(type);

          return allow;
        },
      },
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute("data-id"),
        renderHTML: attrs => ({ "data-id": (attrs as EmojiAttributes).id }),
      },
      native: {
        default: null,
        parseHTML: element => element.getAttribute("data-native"),
        renderHTML: attrs => ({ "data-native": (attrs as EmojiAttributes).native }),
      },
    } satisfies EmojiAttributeSpec;
  },

  parseHTML() {
    return [{
      tag: `span[data-type="${this.name}"]`,
    }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as EmojiAttributes;
    const native = attrs.native as unknown;

    let emoji;
    if (isString(native)) {
      emoji = emojiIndex.findByNative(native);
    }

    return [
      "span",
      mergeAttributes({ "data-type": this.name, class: "font-emoji" }, HTMLAttributes),
      emoji
        ? native
        : `:${attrs.id}:`,
    ];
  },

  renderText({ node }) {
    const attrs = node.attrs as EmojiAttributes;
    return attrs.native || attrs.id;
  },

  addCommands() {
    return {
      insertEmoji: (emoji: EmojiEntrySkin) => ({ chain }) => {
        return chain()
          .insertContent(emoji.native)
          .focus()
          .run();
      },

      startEmoji: () => ({ chain }) => {
        const suggestionChar = this.options.suggestion.char;
        if (!suggestionChar) return false;
        return chain()
          .insertContent(suggestionChar)
          .focus()
          .run();
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
      new Plugin({
        key: EmojiPluginKey,
        props: {
          handleDoubleClickOn: (view, position, node) => {
            if (node.type !== this.type) return false;

            const range = {
              from: position,
              to: position + node.nodeSize,
            };
            return this.editor.commands.setTextSelection(range);
          },
        },
        appendTransaction: (transactions, oldState, newState) => {
          // Pasting of supported native emojis will be converted into emoji nodes
          const docChanges = transactions.some(transaction => transaction.docChanged) && !oldState.doc.eq(newState.doc);
          if (!docChanges) return;

          const { tr } = newState;
          const transform = combineTransactionSteps(oldState.doc, [...transactions]);
          const changes = getChangedRanges(transform);
          changes.forEach(({ newRange }) => {
            const isInsideCode = newState.doc.resolve(newRange.from).parent.type.spec.code;
            if (isInsideCode) return;

            const nodesInChangedRanges = findChildrenInRange(
              newState.doc,
              newRange,
              node => node.type.isText,
            );

            nodesInChangedRanges.forEach(({ node, pos }) => {
              if (!node.text) return;

              const matches = node.text.matchAll(emojiRegex);
              for (const match of matches) {
                if (match.index === undefined) continue;

                const native = match[0];
                const emojiSkin = emojiIndex.findByNative(native);
                if (!emojiSkin) continue;

                const emojiEntry = emojiIndex.findBySkin(emojiSkin);
                if (!emojiEntry) continue;

                const from = tr.mapping.map(pos + match.index);
                const to = from + native.length;

                const attrs: EmojiAttributes = {
                  id: emojiEntry.id,
                  native: emojiSkin.native,
                };
                const emojiNode = this.type.create(attrs);
                tr.replaceRangeWith(from, to, emojiNode);
              }
            });
          });

          if (!tr.steps.length) {
            return;
          }

          return tr;
        },
      }),
    ];
  },
}).configure({
  suggestion: createSuggestionRenderer(EmojiSearchList),
});
